/**
 * MODO LOCAL — detección runtime.
 *
 * La POS se despliega en DOS lugares con el MISMO bundle:
 *  - Firebase Hosting (nube, https) → modo normal, byte-idéntico a hoy.
 *  - Servida por el AGENTE en la LAN (http://IP:7070) → modo local/offline.
 *
 * `IS_LOCAL` es SÍNCRONO (disponible antes del primer render, clave para inyectar el
 * user sintético en el boot) y CERO-riesgo para la nube: cualquier deploy de Hosting es
 * https y sin :7070 → IS_LOCAL=false → los caminos Firestore/Storage/App Check quedan
 * intactos. Same-origin: como la POS la sirve el agente, todo va por rutas RELATIVAS
 * (fetch('/orders'), '/menu', new EventSource('/events')) → sin CORS ni mixed-content.
 */
// El agente auto-salta de puerto si el 7070 está ocupado (típico: AnyDesk usa el 7070) → aceptamos
// el RANGO 7070–7099, no solo el 7070. Sigue siendo CERO-riesgo: la nube es https (protocol !=
// "http:" → false) y el dev corre en :3000 (fuera del rango → false). El 7070 de siempre da true.
export const IS_LOCAL =
  typeof window !== "undefined" &&
  window.location.protocol === "http:" &&
  Number(window.location.port) >= 7070 &&
  Number(window.location.port) <= 7099;

// La PROPIA PC del local (el agente, acceso directo del escritorio) entra por localhost;
// los celulares de los mozos por la IP de la LAN. Sirve para reservar acciones de manager
// (ej. cierre del día) a la PC del local y no exponerlas en el celu de cada mozo.
export const IS_LOCALHOST =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

// ── Token de pairing ──
// La PC del agente (localhost) entra sin token (confiada). Los celulares reciben el token
// por ?t= al escanear el QR (una vez) → lo guardamos y lo mandamos en cada request. Así un
// dispositivo cualquiera de la WiFi (sin el QR) no puede tocar el sistema.
(function readToken() {
  if (typeof window === "undefined") return;
  try {
    const t = new URLSearchParams(window.location.search).get("t");
    if (t) localStorage.setItem("agentToken", t);
  } catch (_) {}
})();

export function agentToken() {
  try { return (typeof localStorage !== "undefined" && localStorage.getItem("agentToken")) || ""; } catch (_) { return ""; }
}

// ── Nombre del mozo (por dispositivo) ──
// En modo local no hay login por mozo; guardamos el nombre en localStorage del device
// (el celu de cada mozo) para estampar las órdenes con quién las tomó. Liviano; la
// versión con login real es la feature Empleados.
export function getWaiterName() {
  try { return (typeof localStorage !== "undefined" && localStorage.getItem("comandasWaiter")) || ""; } catch (_) { return ""; }
}
export function setWaiterName(name) {
  try {
    const v = String(name || "").trim().slice(0, 40);
    if (v) localStorage.setItem("comandasWaiter", v);
    else localStorage.removeItem("comandasWaiter");
    return v;
  } catch (_) { return ""; }
}

// ── Presets de ajustes (adicionales/descuentos frecuentes) ──
// Guardados en el device (localStorage) para no re-escribir "Descuento efectivo 10%" cada vez.
export function getAdjustPresets() {
  try {
    const raw = (typeof localStorage !== "undefined" && localStorage.getItem("comandasAdjPresets")) || "[]";
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch (_) { return []; }
}
export function saveAdjustPreset(preset) {
  try {
    const list = getAdjustPresets();
    const dup = list.some(
      (p) =>
        (p.label || "") === (preset.label || "") &&
        p.kind === preset.kind &&
        (p.mode || "") === (preset.mode || "") &&
        Number(p.value) === Number(preset.value)
    );
    if (!dup) {
      list.push({ label: preset.label, kind: preset.kind, mode: preset.mode, value: preset.value });
      localStorage.setItem("comandasAdjPresets", JSON.stringify(list.slice(-12)));
    }
    return getAdjustPresets();
  } catch (_) { return getAdjustPresets(); }
}
export function removeAdjustPreset(idx) {
  try {
    const list = getAdjustPresets();
    list.splice(idx, 1);
    localStorage.setItem("comandasAdjPresets", JSON.stringify(list));
    return list;
  } catch (_) { return getAdjustPresets(); }
}

// fetch al agente con el token en el header. Para SSE (EventSource no admite headers) va
// por query: usar tokenQuery().
export function agentFetch(path, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  const t = agentToken();
  if (t) headers["X-Agent-Token"] = t;
  // TECHO por pedido, y SOLO para los GET. Sin esto, un agente que acepta el TCP pero no contesta
  // (arrancando, con el disco ocupado) dejaba a la POS en "conectando…" hasta que se rindiera el
  // navegador —minutos— y los 12 reintentos de arranque nunca pasaban del primero.
  // NUNCA en POST /print ni POST /orders: ahí un fetch abortado es AMBIGUO (el pedido pudo haber
  // llegado y salido el papel) y todo el mecanismo de SIN-CONFIRMAR está calibrado para eso.
  // AbortController a mano, no AbortSignal.timeout: hay Windows 7 con Chrome viejo.
  const esGet = !opts.method || String(opts.method).toUpperCase() === "GET";
  if (!esGet || opts.signal || typeof AbortController === "undefined") {
    return fetch(path, { cache: "no-store", ...opts, headers });
  }
  const ac = new AbortController();
  const ms = path.indexOf("/health") === 0 ? 2500 : 8000;
  const to = setTimeout(() => { try { ac.abort(); } catch (_) {} }, ms);
  return fetch(path, { cache: "no-store", ...opts, headers, signal: ac.signal })
    .finally(() => clearTimeout(to));
}
export function tokenQuery() {
  const t = agentToken();
  return t ? "t=" + encodeURIComponent(t) : "";
}

// ── ADMIN de impresoras (wizard, SOLO desde la PC del agente) ── El agente exige el header custom
// X-Agent-Admin (fuerza preflight CORS → un pedido cross-site queda bloqueado; es la defensa
// anti-CSRF). Same-origin (la POS servida por el agente en 127.0.0.1:7070) no dispara preflight, así
// que estos fetch andan sin CORS. Solo tienen sentido con IS_LOCAL && IS_LOCALHOST.
function adminFetch(path, opts = {}) {
  const headers = { "X-Agent-Admin": "1", ...(opts.headers || {}) };
  return fetch(path, { cache: "no-store", ...opts, headers });
}
async function adminJson(path, opts) {
  const r = await adminFetch(path, opts);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}
/**
 * Detectar comanderas: how = "usb" (colas de Windows) | "net" (barrido Ethernet).
 * opts.factory: corre el flujo COMPLETO del instalador (fija la IP a una impresora de fábrica en
 * 192.168.123.100). opts.excludeIps: IPs ya registradas (para no ofrecer/pisar la que ya anda).
 */
export function detectPrinters(how, opts = {}) {
  const p = new URLSearchParams();
  p.set("how", how === "net" ? "net" : "usb");
  if (opts.factory) p.set("factory", "1");
  if (opts.excludeIps && opts.excludeIps.length) p.set("exclude", opts.excludeIps.join(","));
  return adminJson(`/printers/detect?${p.toString()}`);
}
/** Imprimir prueba SIN registrar. body = { transport, queueName?|ip?, port? }. */
export function testPrinterAdmin(body) {
  return adminJson("/printers/test", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}
/**
 * Configurar una impresora de fábrica POR USB: le fija una IP de la subred del local por el cable USB
 * (no depende de la red). Devuelve { ok, ip }. Después se pasa a Ethernet y se prueba/registra.
 */
export function usbSetIp(body) {
  return adminJson("/printers/usb-setip", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}
/**
 * Cambiar la IP de una impresora que ya está en la red (resolver IPs repetidas). La alcanza en su IP
 * actual y le fija una libre distinta. Devuelve { ok, ip }.
 */
export function changePrinterIp(currentIp) {
  return adminJson("/printers/change-ip", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ currentIp }),
  });
}
/** Registrar la comandera (mintea printerId para USB, la sube a la nube). */
export function registerPrinterAdmin(body) {
  return adminJson("/printers/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

// Trae {ownerUid, menuId, printer, version} del agente. Reintenta un rato por si el
// servicio del agente todavía está arrancando. No cachea el fallo.
export async function getHealth(retries = 12, delayMs = 500) {
  for (let i = 0; i < retries; i++) {
    try {
      const r = await agentFetch("/health");
      if (r.ok) {
        const h = await r.json();
        if (h && h.agent) return h;
      }
    } catch (_) {
      /* agente todavía no responde; reintenta */
    }
    await new Promise((res) => setTimeout(res, delayMs));
  }
  return null;
}
